import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { User, CheckCircle, XCircle, Clock, Mail, Trophy, DollarSign, TrendingUp, ChevronDown, ChevronUp, Eye, Users, Calendar, ArrowRightLeft } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { TalentApplicationProfile } from './TalentApplicationProfile';
import { useDealApplications } from '@/hooks/useDealApplications';
import { AITalentRecommendations } from './AITalentRecommendations';
import { CounterOffer } from '@/hooks/useCounterOffers';

// Helper component for viewing full talent profile
const TalentProfileDialog = ({ talentProfileId, isOpen, onClose }: { talentProfileId: string; isOpen: boolean; onClose: () => void }) => {
  const [talentData, setTalentData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTalent = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('talents')
          .select(`
            *,
            profiles!inner (
              bio,
              location,
              is_verified,
              avatar_url
            )
          `)
          .eq('profile_id', talentProfileId)
          .single();

        if (error) throw error;
        
        const talent = {
          ...data,
          bio: data.profiles?.bio || undefined,
          location: data.profiles?.location || undefined,
          is_verified: data.profiles?.is_verified || false,
          avatar_url: data.profiles?.avatar_url || undefined,
          career_highlights: Array.isArray(data.career_highlights) ? data.career_highlights : [],
          sponsors: Array.isArray(data.sponsors) ? data.sponsors : [],
          achievements: Array.isArray(data.achievements) ? data.achievements : [],
        };
        
        setTalentData(talent);
      } catch (error) {
        console.error('Error fetching talent:', error);
      } finally {
        setLoading(false);
      }
    };

    if (isOpen && talentProfileId) {
      fetchTalent();
    }
  }, [talentProfileId, isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Talent Profile</DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Clock className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : talentData ? (
          <TalentApplicationProfile talent={talentData} />
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            Unable to load talent profile
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

interface TalentInfo {
  id: string;
  name: string;
  first_name?: string;
  last_name?: string;
  sport?: string;
  photo?: string;
  total_followers?: number;
  total_revenue?: number;
  total_holdings?: number;
  avg_business_impact?: number;
  tier?: string;
  status?: string;
  height?: string;
  weight?: string;
  nationality?: string;
  competition_level?: string;
  college_university?: string;
  draft_round?: number;
  draft_year?: number;
  achievements?: string[];
  career_highlights?: any[];
  sponsors?: any[];
  instagram_handle?: string;
  media_kit_url?: string;
  stats?: any;
  bio?: string;
  location?: string;
  is_verified?: boolean;
  avatar_url?: string;
}

interface BrandDealTalentTabProps {
  deal: {
    id: string;
    status: string;
    talent_profile_id?: string;
    updated_at: string;
  };
  onAccept: (dealId: string, talentProfileId: string) => Promise<{ success: boolean }>;
  onReject: (dealId: string) => Promise<{ success: boolean }>;
  onRefresh?: () => void;
}

export const BrandDealTalentTab: React.FC<BrandDealTalentTabProps> = ({
  deal,
  onAccept,
  onReject,
  onRefresh
}) => {
  const [talent, setTalent] = useState<TalentInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [showFullProfile, setShowFullProfile] = useState(false);
  const [selectedTalentId, setSelectedTalentId] = useState<string | null>(null);
  const [counterOffersMap, setCounterOffersMap] = useState<Record<string, CounterOffer[]>>({});
  const { toast } = useToast();
  const { applications, loading: applicationsLoading, acceptApplication, rejectApplication } = useDealApplications(deal.id);

  // Fetch counter offers for all applications
  useEffect(() => {
    const fetchAllCounterOffers = async () => {
      if (applications.length === 0) return;
      const map: Record<string, CounterOffer[]> = {};
      await Promise.all(
        applications.map(async (app) => {
          const { data } = await supabase
            .from('deal_counter_offers')
            .select('*')
            .eq('deal_application_id', app.id)
            .order('created_at', { ascending: false });
          if (data && data.length > 0) {
            map[app.id] = data as CounterOffer[];
          }
        })
      );
      setCounterOffersMap(map);
    };
    fetchAllCounterOffers();
  }, [applications]);
  
  // Separate applications by initiation type
  const talentInitiatedApplications = applications.filter(
    app => app.status === 'pending' && app.initiated_by === 'talent'
  );
  
  const brandInitiatedApplications = applications.filter(
    app => app.status === 'pending' && app.initiated_by === 'brand'
  );

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  const getTierColor = (tier: string) => {
    switch (tier?.toLowerCase()) {
      case 'gold': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300';
      case 'silver': return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
      case 'bronze': return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300';
      case 'platinum': return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300';
      default: return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300';
    }
  };

  useEffect(() => {
    const fetchTalentInfo = async () => {
      if (!deal.talent_profile_id) {
        setLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('talents')
          .select(`
            id,
            name,
            first_name,
            last_name,
            sport,
            photo,
            position,
            team_name,
            total_followers,
            total_revenue,
            total_holdings,
            avg_business_impact,
            tier,
            status,
            height,
            weight,
            nationality,
            competition_level,
            college_university,
            draft_round,
            draft_year,
            achievements,
            career_highlights,
            sponsors,
            instagram_handle,
            media_kit_url,
            stats,
            profile_id,
            profiles!inner (
              bio,
              location,
              is_verified,
              avatar_url
            )
          `)
          .eq('profile_id', deal.talent_profile_id)
          .single();

        if (error) throw error;
        
        // Flatten the profile data and ensure correct types
        const talentData: TalentInfo = {
          ...data,
          bio: data.profiles?.bio || undefined,
          location: data.profiles?.location || undefined,
          is_verified: data.profiles?.is_verified || false,
          avatar_url: data.profiles?.avatar_url || undefined,
          career_highlights: Array.isArray(data.career_highlights) ? data.career_highlights : [],
          sponsors: Array.isArray(data.sponsors) ? data.sponsors : [],
          achievements: Array.isArray(data.achievements) ? data.achievements : [],
        };
        
        setTalent(talentData);
      } catch (error) {
        console.error('Error fetching talent:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTalentInfo();
  }, [deal.talent_profile_id]);

  const handleAccept = async () => {
    if (!deal.talent_profile_id) return;
    
    setProcessing(true);
    try {
      await onAccept(deal.id, deal.talent_profile_id);
      toast({
        title: "Talent Accepted!",
        description: "The talent has been successfully linked to this deal.",
      });
      onRefresh?.();
    } catch (error) {
      toast({
        title: "Failed to Accept",
        description: "There was an error accepting the talent. Please try again.",
        variant: "destructive",
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    setProcessing(true);
    try {
      await onReject(deal.id);
      toast({
        title: "Application Rejected",
        description: "The talent application has been rejected and the deal is now available again.",
      });
      onRefresh?.();
    } catch (error) {
      toast({
        title: "Failed to Reject",
        description: "There was an error rejecting the application. Please try again.",
        variant: "destructive",
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleAcceptApplication = async (applicationId: string, talentProfileId: string) => {
    setProcessing(true);
    try {
      const result = await acceptApplication(applicationId);
      if (result.success) {
        toast({
          title: "Talent Accepted!",
          description: "The talent has been successfully linked to this deal.",
        });
      }
      if (onRefresh) await onRefresh();
    } finally {
      setProcessing(false);
    }
  };

  const handleRejectApplication = async (applicationId: string) => {
    setProcessing(true);
    try {
      const result = await rejectApplication(applicationId);
      if (result.success && onReject) {
        await onReject(deal.id);
      }
      if (onRefresh) await onRefresh();
    } finally {
      setProcessing(false);
    }
  };

  const handleRevokeInvitation = async (applicationId: string) => {
    setProcessing(true);
    try {
      const result = await rejectApplication(applicationId);
      if (result.success) {
        toast({
          title: "Invitation Revoked",
          description: "The talent invitation has been successfully withdrawn.",
        });
        if (onRefresh) await onRefresh();
      } else {
        throw new Error("Failed to revoke invitation");
      }
    } catch (error) {
      toast({
        title: "Failed to Revoke",
        description: "There was an error revoking the invitation. Please try again.",
        variant: "destructive",
      });
    } finally {
      setProcessing(false);
    }
  };

  if (loading || applicationsLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-8">
            <Clock className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  // Deal is active - show confirmed talent with full profile
  if (deal.status === 'active' && deal.talent_profile_id && talent) {
    return (
      <div className="space-y-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Badge className="bg-green-500/10 text-green-700 border-green-500/20">
                <CheckCircle className="h-3 w-3 mr-1" />
                Active Partnership
              </Badge>
              <span className="text-sm text-muted-foreground">
                This deal is active with {talent.name}. Manage tasks and deliverables in the other tabs.
              </span>
            </div>
          </CardContent>
        </Card>

        <TalentApplicationProfile talent={talent} />
      </div>
    );
  }

  // Show applications for available/pending deals
  if (applications.length === 0) {
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="p-6">
            <div className="text-center py-8">
              <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Applications Yet</h3>
              <p className="text-muted-foreground">
                This deal is available in the marketplace. Talented athletes can apply for this opportunity.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* AI-Recommended Talents - Show even without applications */}
        {(deal.status === 'available' || deal.status === 'pending') && (
          <AITalentRecommendations 
            dealId={deal.id}
            onViewProfile={(profileId) => setSelectedTalentId(profileId)}
          />
        )}

        {/* Full Profile Dialog */}
        {selectedTalentId && (
          <TalentProfileDialog
            talentProfileId={selectedTalentId}
            isOpen={!!selectedTalentId}
            onClose={() => setSelectedTalentId(null)}
          />
        )}
      </div>
    );
  }

  // Show applications in two sections
  return (
    <div className="space-y-6">
      {/* Section 1: Applications Pending Brand Approval */}
      {talentInitiatedApplications.length > 0 && (
        <div className="space-y-4">
          <div className="border-b pb-2">
            <h3 className="text-lg font-semibold">
              Applications Pending Brand Approval ({talentInitiatedApplications.length})
            </h3>
            <p className="text-sm text-muted-foreground">
              Athletes who have applied for this deal - review and accept the best fit
            </p>
          </div>
          
          <div className="grid gap-4">
            {talentInitiatedApplications.map((application) => (
              <Card key={application.id} className="overflow-hidden">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <Avatar className="h-16 w-16">
                      <AvatarImage src={application.talent_photo || undefined} alt={application.talent_name} />
                      <AvatarFallback>{application.talent_name.substring(0, 2).toUpperCase()}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h4 className="text-lg font-semibold">{application.talent_name}</h4>
                          <div className="flex flex-wrap items-center gap-2 mt-1">
                            <Badge variant="secondary">{application.talent_sport}</Badge>
                            <Badge variant="outline" className={getTierColor(application.talent_tier)}>
                              {application.talent_tier} Tier
                            </Badge>
                            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                              Talent Applied
                            </Badge>
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4 mt-4">
                        <div className="flex items-center gap-2 text-sm">
                          <Mail className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">{formatNumber(application.talent_followers)}</span>
                          <span className="text-muted-foreground">followers</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          <span className="text-muted-foreground">
                            Applied {new Date(application.applied_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>

                      {/* Counter Offer Details */}
                      {counterOffersMap[application.id] && counterOffersMap[application.id].length > 0 && (() => {
                        const latestOffer = counterOffersMap[application.id][0];
                        return (
                          <div className="mt-4 p-3 rounded-lg border border-primary/20 bg-primary/5">
                            <div className="flex items-center gap-2 mb-2">
                              <ArrowRightLeft className="h-4 w-4 text-primary" />
                              <span className="text-sm font-semibold text-primary">Counter Offer</span>
                              <Badge variant="outline" className="text-xs capitalize">{latestOffer.status}</Badge>
                            </div>
                            <div className="grid grid-cols-2 gap-3 text-sm">
                              <div>
                                <p className="text-muted-foreground text-xs">Proposed Value</p>
                                <p className="font-semibold">{formatCurrency(latestOffer.proposed_value)}</p>
                              </div>
                              {latestOffer.proposed_deal_type && (
                                <div>
                                  <p className="text-muted-foreground text-xs">Deal Type</p>
                                  <p className="font-medium capitalize">{latestOffer.proposed_deal_type}</p>
                                </div>
                              )}
                              {latestOffer.proposed_deadline && (
                                <div>
                                  <p className="text-muted-foreground text-xs">Proposed Deadline</p>
                                  <p className="font-medium">{new Date(latestOffer.proposed_deadline).toLocaleDateString()}</p>
                                </div>
                              )}
                              {latestOffer.proposed_terms && (
                                <div className="col-span-2">
                                  <p className="text-muted-foreground text-xs">Terms</p>
                                  <p className="font-medium">{latestOffer.proposed_terms}</p>
                                </div>
                              )}
                            </div>
                            {latestOffer.message && (
                              <div className="mt-2 pt-2 border-t border-primary/10">
                                <p className="text-muted-foreground text-xs">Message</p>
                                <p className="text-sm italic">"{latestOffer.message}"</p>
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      <div className="flex gap-2 mt-4">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setSelectedTalentId(application.talent_profile_id)}
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          View Full Profile
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleAcceptApplication(application.id, application.talent_profile_id)}
                          disabled={processing}
                        >
                          Accept
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleRejectApplication(application.id)}
                          disabled={processing}
                        >
                          Reject
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Section 2: Applications Pending Talent Approval */}
      {brandInitiatedApplications.length > 0 && (
        <div className="space-y-4">
          <div className="border-b pb-2">
            <h3 className="text-lg font-semibold">
              Applications Pending Talent Approval ({brandInitiatedApplications.length})
            </h3>
            <p className="text-sm text-muted-foreground">
              Athletes you've invited - waiting for their response
            </p>
          </div>
          
          <div className="grid gap-4">
            {brandInitiatedApplications.map((application) => (
              <Card key={application.id} className="overflow-hidden">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <Avatar className="h-16 w-16">
                      <AvatarImage src={application.talent_photo || undefined} alt={application.talent_name} />
                      <AvatarFallback>{application.talent_name.substring(0, 2).toUpperCase()}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h4 className="text-lg font-semibold">{application.talent_name}</h4>
                          <div className="flex flex-wrap items-center gap-2 mt-1">
                            <Badge variant="secondary">{application.talent_sport}</Badge>
                            <Badge variant="outline" className={getTierColor(application.talent_tier)}>
                              {application.talent_tier} Tier
                            </Badge>
                            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                              Brand Invited
                            </Badge>
                            <Badge variant="secondary">
                              Awaiting Response
                            </Badge>
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4 mt-4">
                        <div className="flex items-center gap-2 text-sm">
                          <Mail className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">{formatNumber(application.talent_followers)}</span>
                          <span className="text-muted-foreground">followers</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          <span className="text-muted-foreground">
                            Invited {new Date(application.applied_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>

                      <div className="space-y-2 mt-4">
                        <Badge variant="secondary" className="w-full justify-center">
                          <Clock className="h-4 w-4 mr-2" />
                          Pending Athlete Approval
                        </Badge>
                        <p className="text-xs text-muted-foreground text-center">
                          Waiting for {application.talent_name} to accept or decline this pairing
                        </p>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex-1"
                            onClick={() => setSelectedTalentId(application.talent_profile_id)}
                            disabled={processing}
                          >
                            <Eye className="h-4 w-4 mr-2" />
                            View Full Profile
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            className="flex-1"
                            onClick={() => handleRevokeInvitation(application.id)}
                            disabled={processing}
                          >
                            <XCircle className="h-4 w-4 mr-2" />
                            {processing ? "Revoking..." : "Revoke Invitation"}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Empty state when no pending applications */}
      {talentInitiatedApplications.length === 0 && brandInitiatedApplications.length === 0 && (
        <Card>
          <CardContent className="p-6">
            <div className="text-center py-8">
              <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Pending Applications</h3>
              <p className="text-muted-foreground">
                No athletes have applied yet, and you haven't invited any talent.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* AI-Recommended Talents section */}
      {(deal.status === 'available' || deal.status === 'pending') && (
        <AITalentRecommendations 
          dealId={deal.id}
          onViewProfile={(profileId) => setSelectedTalentId(profileId)}
        />
      )}

      {/* Full Profile Dialog */}
      {selectedTalentId && (
        <TalentProfileDialog
          talentProfileId={selectedTalentId}
          isOpen={!!selectedTalentId}
          onClose={() => setSelectedTalentId(null)}
        />
      )}
    </div>
  );
};
